import type { ComponentSchema, FieldSchema } from '../../../../../schema-types'

/**
 * Home sections Shegun renders with its own markup (sections/<type>/themes/furniture-1.vue), described by
 * the fields those overrides read. Colour fields the overrides ignore are left out: the theme keeps its ink
 * and paper tokens.
 */

const CONTENT_PAGES = ['home', 'shop', 'category', 'categories', 'product', 'cart', 'blog']

/** Icons Shegun's kit draws; shared names (shipping, secure, refund, support) map onto them too. */
const ICON_OPTIONS = ['truck', 'cash', 'phone', 'shield', 'star', 'heart', 'package', 'check', 'store', 'map-pin', 'mail', 'sparkle', 'clock', 'bag', 'user', 'search', 'refresh', 'lock', 'tag']
  .map(value => ({ label: value.replace('-', ' '), value }))

const headingFields = (defaults: { eyebrow?: string, title?: string, subtitle?: string } = {}): FieldSchema[] => [
  { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: defaults.eyebrow ?? '', help: 'Small line above the title.' },
  { key: 'title', label: 'Title', type: 'localized-string', default: defaults.title ?? '' },
  { key: 'subtitle', label: 'Subtitle', type: 'localized-text', default: defaults.subtitle ?? '' },
]

const POINT_SCHEMA: FieldSchema[] = [
  { key: 'icon', label: 'Icon', type: 'icon', default: 'check', options: ICON_OPTIONS },
  { key: 'title', label: 'Title', type: 'localized-string' },
  { key: 'body', label: 'Text', type: 'localized-text' },
]

export const bannerCarouselSchema: ComponentSchema = {
  type: 'banner-carousel',
  label: 'Banner carousel',
  description: 'Picture slides (their text is part of the image) crossfading in a square frame, with counter, arrows and pause.',
  supportedPages: ['home', 'shop', 'category', 'categories'],
  variants: [{ id: 'v1', label: 'Crossfading slides' }],
  commonFields: [
    { key: 'title', label: 'Name for screen readers', type: 'string', default: 'Offers' },
    {
      key: 'slides',
      label: 'Slides',
      type: 'array',
      itemSchema: [
        { key: 'image', label: 'Picture', type: 'image', required: true, help: 'Square, about 1200 × 1200 px.' },
        { key: 'mobileImage', label: 'Phone picture', type: 'image', help: 'Optional.' },
        { key: 'alt', label: 'Picture description', type: 'string', help: 'What the banner says, for screen readers.' },
        { key: 'link', label: 'Link', type: 'string', placeholder: '/shop' },
      ],
      default: [],
    },
    { key: 'autoplay', label: 'Play automatically', type: 'boolean', default: true },
    { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 5, dependency: { field: 'autoplay', value: true } },
  ],
}

export const categoryBentoSchema: ComponentSchema = {
  type: 'category-bento',
  label: 'Category mosaic',
  description: 'Photo tiles linking to categories: the tiles you list, else the store’s categories.',
  supportedPages: CONTENT_PAGES,
  variants: [
    { id: 'v1', label: 'V1 — Row of photo tiles' },
    { id: 'v2', label: 'V2 — Lookbook mosaic (first tile large)' },
  ],
  commonFields: [
    ...headingFields({ eyebrow: 'Browse', title: 'Shop by room' }),
    {
      key: 'tiles',
      label: 'Tiles',
      type: 'array',
      help: 'Empty = the store’s categories.',
      itemSchema: [
        { key: 'categoryId', label: 'Category', type: 'category-select', help: 'Sets the link, name and picture unless filled below.' },
        { key: 'title', label: 'Name', type: 'localized-string' },
        { key: 'tagline', label: 'Tagline (mosaic)', type: 'localized-string' },
        { key: 'image', label: 'Picture', type: 'image' },
        { key: 'link', label: 'Link', type: 'string', placeholder: '/category/…' },
      ],
      default: [],
    },
    { key: 'limit', label: 'Categories shown (automatic tiles)', type: 'number', default: 8 },
  ],
}

export const comboSpotlightSchema: ComponentSchema = {
  type: 'combo-spotlight',
  label: 'Product spotlight',
  description: 'One product or bundle as an editorial spread: large photo, price, saving and Add to cart.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Editorial spread' }],
  commonFields: [
    ...headingFields({ eyebrow: 'Showroom pick', title: 'Piece of the week' }),
    { key: 'productId', label: 'Product', type: 'product-select', help: 'Hidden until a product is picked.' },
    { key: 'description', label: 'Text', type: 'localized-text', default: '' },
    { key: 'mrpLabel', label: 'Regular price label', type: 'localized-string', default: 'Regular price' },
    { key: 'savingLabel', label: 'Saving label', type: 'localized-string', default: 'You save' },
    { key: 'detailsLabel', label: 'Details link text', type: 'localized-string', default: 'View details' },
  ],
}

export const corporateBandSchema: ComponentSchema = {
  type: 'corporate-band',
  label: 'Bulk order band',
  description: 'An ink band with a short pitch and one button, e.g. for team, event or wholesale orders.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Ink band' }],
  commonFields: [
    ...headingFields({ eyebrow: 'For offices', title: 'Office and project orders' }).filter(f => f.key !== 'subtitle'),
    { key: 'text', label: 'Text', type: 'localized-text', default: 'Furnishing an office, a hotel or a new flat? Get project pricing and custom sizes.' },
    { key: 'cta.label', label: 'Button text', type: 'localized-string', default: 'Contact us' },
    { key: 'cta.url', label: 'Button link', type: 'string', default: '/contact' },
  ],
}

export const productRailSchema: ComponentSchema = {
  type: 'product-rail',
  label: 'Product rail',
  description: 'Product cards from new arrivals, deals, a category or your own pick, as a grid or a swipe row.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Product cards' }],
  commonFields: [
    ...headingFields({ eyebrow: 'From the workshop', title: 'New arrivals' }),
    { key: 'source', label: 'Products', type: 'product-source', default: { source: 'new', limit: 8 } },
    {
      key: 'layout',
      label: 'Layout',
      type: 'select',
      options: [
        { label: 'Grid (four up)', value: 'grid' },
        { label: 'Swipe row', value: 'rail' },
      ],
      default: 'grid',
    },
    { key: 'showViewAll', label: 'Show “View all”', type: 'boolean', default: true },
    { key: 'viewAllLabel', label: '“View all” text', type: 'string', default: 'View all', dependency: { field: 'showViewAll', value: true } },
  ],
}

export const promisesSchema: ComponentSchema = {
  type: 'promises',
  label: 'Promises strip',
  description: 'Two to four store promises in a hairline-ruled row: icon, title and a short line.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Ruled row' }],
  commonFields: [
    ...headingFields(),
    {
      key: 'items',
      label: 'Promises',
      type: 'array',
      itemSchema: POINT_SCHEMA,
      default: [
        { icon: 'truck', title: 'Fast delivery', body: 'Quick delivery across the city.' },
        { icon: 'cash', title: 'Pay on delivery', body: 'Pay in cash when your order arrives.' },
        { icon: 'refresh', title: 'Easy returns', body: 'Wrong size? Swap it within 7 days.' },
        { icon: 'phone', title: 'Here to help', body: 'Call or message us any day.' },
      ],
    },
  ],
}

export const reviewsSchema: ComponentSchema = {
  type: 'reviews',
  label: 'Customer reviews',
  description: 'A swipe row of quote cards: rating, title, quote, name and the product. Store reviews replace these once available.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Quote cards' }],
  commonFields: [
    ...headingFields({ eyebrow: 'Reviews', title: 'What our customers say' }),
    {
      key: 'items',
      label: 'Reviews',
      type: 'array',
      help: 'Real reviews from your customers, with their permission.',
      itemSchema: [
        { key: 'rating', label: 'Rating (1–5)', type: 'number', default: 5 },
        { key: 'title', label: 'Title', type: 'localized-string' },
        { key: 'body', label: 'Review', type: 'localized-text' },
        { key: 'name', label: 'Customer name', type: 'string' },
        { key: 'productId', label: 'Product', type: 'product-select' },
      ],
      default: [],
    },
    { key: 'limit', label: 'Most reviews shown', type: 'number', default: 6 },
    { key: 'minRating', label: 'Lowest rating shown (store reviews)', type: 'number', default: 4 },
  ],
}

/** Story points; the materials comparison (v2) also reads a wood colour, a facts line and a link per point. */
const STORY_POINT_SCHEMA: FieldSchema[] = [
  ...POINT_SCHEMA,
  { key: 'swatch', label: 'Wood colour (materials)', type: 'color', help: 'Shown as a swatch on the comparison card.' },
  { key: 'note', label: 'Facts line (materials)', type: 'localized-string', placeholder: 'Hardness: very high · Price: ৳৳৳', help: 'Separate facts with ·' },
  { key: 'link', label: 'Link (materials)', type: 'string', placeholder: '/shop?search=segun' },
]

export const storySchema: ComponentSchema = {
  type: 'story',
  label: 'Story band',
  description: 'A 4:5 photo beside the heading and two to four numbered points, with an optional button.',
  supportedPages: CONTENT_PAGES,
  variants: [{ id: 'v1', label: 'Photo and numbered points' }, { id: 'v2', label: 'Materials comparison' }],
  commonFields: [
    ...headingFields({ eyebrow: 'Our story', title: 'Made from wood that lasts generations' }),
    { key: 'image', label: 'Picture', type: 'image', help: 'Portrait, 4:5.' },
    {
      key: 'points',
      label: 'Points',
      type: 'array',
      itemSchema: STORY_POINT_SCHEMA,
      default: [
        { icon: 'sparkle', title: 'Seasoned hardwood', body: 'Kiln-dried segun and mehogoni that does not warp in the monsoon.' },
        { icon: 'shield', title: 'Five-year warranty', body: 'Joints and frames covered, with free service visits in Dhaka.' },
        { icon: 'truck', title: 'Delivered and assembled', body: 'Our own team carries it in and sets it up.' },
      ],
    },
    { key: 'ctaLabel', label: 'Button text', type: 'localized-string', default: '' },
    { key: 'ctaLink', label: 'Button link', type: 'string', default: '' },
  ],
}
